// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * Override this handler to parse product videos
 */
import isApiError from "@insite/client-framework/Common/isApiError";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { addToChainAfter, replaceInChain } from "@insite/client-framework/HandlerCreator";
import { StorefrontAccess } from "@insite/client-framework/Services/SettingsService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    chain,
    DispatchCompleteLoadProduct,
    PopulateApiParameter,
    RequestDataFromApi
} from "@insite/client-framework/Store/Data/Products/Handlers/LoadProductByPath";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import setCustomProductState from "@mcmc/App/Handlers/Pages/ProductDetails/SetCustomProductState";
import { getProductByPath } from "@mcmc/App/Services/ProductService";
import { DocumentGroup, VideoDocumentModel } from "@mcmc/App/Store/Reducers/ProductDetailsReducer";

export const CustomPopulateApiParameter: typeof PopulateApiParameter = props => {
    if (props.apiParameter.expand) {
        props.apiParameter.expand.push("properties", "expandEvents" as any);
    } else {
        props.apiParameter.expand = ["properties", "expandEvents" as any];
    }
};

function getVideosList(product: ProductModel) {
    return product.properties.videoLink ? product.properties.videoLink.split("|") : [];
}

function parseProductVideos(product: ProductModel): VideoDocumentModel[] {
    const documentVideos = getVideosList(product);
    const videos: VideoDocumentModel[] = [];
    documentVideos.every((videoLink, index) => {
        const videoId = videoLink && videoLink.substr(videoLink.lastIndexOf("/") + 1);
        videos.push({
            id: index,
            link: videoLink,
            thumbnailPath: `https://img.youtube.com/vi/${videoId}/default.jpg`,
        });
        return true;
    });
    return videos;
}

function parseProductDocuments({ documents }: ProductModel): DocumentGroup[] {
    const dictionary: Dictionary<DocumentGroup> = {};
    const documentGroups: DocumentGroup[] = [];
    documents?.every(doc => {
        const docType = doc.documentType && doc.documentType.toLowerCase();
        let docGroup = dictionary[docType];
        if (!docGroup) {
            docGroup = dictionary[docType] = {
                groupName: doc.documentType,
                documents: [],
            };
            documentGroups.push(docGroup);
        }
        docGroup.documents.push(doc);
        return true;
    });
    return documentGroups;
}

function parseProductPromotionProperties({ properties }: ProductModel) {
    return properties.promoMessage
        ? {
              message: properties.promoMessage,
              color: properties.promoBackgroundColor,
          }
        : undefined;
}

export function parseProductCustomState(product: ProductModel, isEventParent?: boolean) {
    const documentGroups = parseProductDocuments(product);
    const videos = parseProductVideos(product);
    let selectedVideoIndex = -1;
    if (videos && videos.length > 0 && product?.images?.length === 0) {
        selectedVideoIndex = 0;
    }

    const specSheetLink = product.documents?.find(doc => doc.documentType === "SpecificationSheet")?.filePath;
    const promotionMessage = parseProductPromotionProperties(product);

    const isEvent = isEventParent || product.properties.isEvent?.toLowerCase() === "true";
    let eventData;
    if (isEvent) {
        eventData = {
            duration: product.properties.duration,
            coordinatorEmail: product.properties.coordinatorEmail,
            coordinatorInitial: product.properties.coordinatorInitials,
            category: product.properties.eventCategory,
            endDate: product.properties.eventEndDate,
            startDate: product.properties.eventStartDate,
            endDateWithOffset: product.properties.eventEndDateFormatted,
            startDateWithOffset: product.properties.eventStartDateFormatted,
            location: product.properties.eventLocation,
            thirdPartyRegistration: product.properties.thirdPartyRegistration,
            formType: product.properties.formType,
            eventCity: product.properties.eventCity,
            eventState: product.properties.eventState,
        };
    }

    const quickShipIconUrl = product.properties?.quickShipIconUrl;

    return {
        productId: product.id,
        videos,
        selectedVideoIndex,
        specSheetLink,
        documentGroups,
        promotionMessage,
        isEvent,
        eventData,
        quickShipIconUrl,
    };
}

export const CustomDispatchCompleteLoadProduct: typeof DispatchCompleteLoadProduct = props => {
    if (!props.apiResult) {
        return;
    }

    props.dispatch(setCustomProductState({ customProductStates: [parseProductCustomState(props.apiResult)] }));

    return DispatchCompleteLoadProduct(props);
};

export const CustomRequestDataFromApi: typeof RequestDataFromApi = async props => {
    try {
        props.apiResult = await getProductByPath(props.apiParameter);
    } catch (error) {
        if (isApiError(error) && error.status === 400) {
            props.parameter.onError?.(error.errorJson.message);
            return false;
        }

        const { storefrontAccess } = getSettingsCollection(props.getState()).productSettings;
        if (storefrontAccess === StorefrontAccess.SignInRequiredToBrowse && error.status === 403) {
            // auth timed out with SignInRequiredToBrowsee - throw this up as a 401 so DisplayError reloads the site
            error.status = 401;
            error.errorJson = { message: "error" };
        }

        throw error;
    }
};

addToChainAfter(chain, PopulateApiParameter, CustomPopulateApiParameter);
replaceInChain(chain, DispatchCompleteLoadProduct, CustomDispatchCompleteLoadProduct);
replaceInChain(chain, RequestDataFromApi, CustomRequestDataFromApi);
